import { z } from 'zod';
import { authSchema } from './auth.js';

/**
 * Connection Category Enum
 */
export const connectionCategorySchema = z.enum([
  'SEMANTIC_LAYER',
  'CRM',
  'DATABASE',
  'API',
  'DOCUMENT_STORE',
  'MCP_SERVER',
]);
export type ConnectionCategory = z.infer<typeof connectionCategorySchema>;

/**
 * Connection Provider Enum
 */
export const connectionProviderSchema = z.enum([
  'CUBEJS',
  'SALESFORCE',
  'POSTGRES',
  'SPECIFIC_HOST',
  'SHAREPOINT',
  'VEEVAVAULT',
  'MCP',
]);
export type ConnectionProvider = z.infer<typeof connectionProviderSchema>;

/**
 * MCP Server Connection Config
 */
export const mcpServerConnectionConfigSchema = z.object({
  // url: z.url().refine(
  //   (u) => new URL(u).protocol === 'https:',
  //   { message: 'MCP server URL must use HTTPS' }
  // ),
  url: z.url(),
  auth: authSchema,
});
export type McpServerConnectionConfig = z.infer<typeof mcpServerConnectionConfigSchema>;

/**
 * API Server Connection Config
 */
export const apiServerConnectionConfigSchema = z.object({
  url: z.url(),
  auth: authSchema,
});
export type ApiServerConnectionConfig = z.infer<typeof apiServerConnectionConfigSchema>;

/**
 * Cube Dev Connection Config
 */
export const cubeDevConnectionConfigSchema = z.object({
  host: z.url(),
  tokenSecretIdentifier: z.string().min(1),
});
export type CubeDevConnectionConfig = z.infer<typeof cubeDevConnectionConfigSchema>;

/**
 * Connection Status Enum
 */
export const connectionStatusSchema = z.enum(['active', 'error', 'pending_test', 'disabled']);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/**
 * MCP tool metadata returned from GET /data-connections/:identifier/mcp-tools
 */
export const mcpToolMetadataSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  inputSchema: z
    .looseObject({
      type: z.literal('object'),
      properties: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
});
export type McpToolMetadata = z.infer<typeof mcpToolMetadataSchema>;

export const listMcpToolsResponseSchema = z.object({
  tools: z.array(mcpToolMetadataSchema),
});
export type ListMcpToolsResponse = z.infer<typeof listMcpToolsResponseSchema>;

/**
 * Test Result Schema
 * Handles both legacy format ({ success: boolean }) and current format ({ status: 'success' | 'error' })
 */
export const testResultSchema = z
  .object({
    success: z.boolean().optional(),
    status: z.enum(['success', 'error']).optional(),
    message: z.string(),
    timestamp: z.coerce.date(),
  })
  .transform((data) => ({
    status: (data.status ?? (data.success ? 'success' : 'error')) as 'success' | 'error',
    message: data.message,
    timestamp: data.timestamp,
  }))
  .nullable()
  .optional();
export type TestResult = z.infer<typeof testResultSchema>;

/**
 * Create Data Connection Request Body
 */
export const createDataConnectionBodySchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    connectionCategory: connectionCategorySchema,
    connectionProvider: connectionProviderSchema,
    config: z.record(z.string(), z.unknown()).default({}),
    /**
     * Secrets for the data connection (Key-Value pairs)
     * - Omitted: No secret stored in AWS Secrets Manager
     * - Empty object `{}`: Equivalent to omitted (no secret)
     * - Non-empty `{key: "value", ...}`: Creates a secret in AWS Secrets Manager
     *
     * Example: `{ password: "db-password", apiKey: "sk-123456" }`
     */
    secrets: z.record(z.string(), z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.connectionProvider === 'SPECIFIC_HOST') {
      const configResult = apiServerConnectionConfigSchema.safeParse(data.config);
      if (!configResult.success) {
        ctx.addIssue({
          code: 'custom',
          path: ['config'],
          message: 'SPECIFIC_HOST config must include url and auth',
        });
      }
      if (data.secrets !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['secrets'],
          message: 'SPECIFIC_HOST auth must be configured via config.auth',
        });
      }
    }

    if (data.connectionProvider === 'CUBEJS') {
      const configResult = cubeDevConnectionConfigSchema.safeParse(data.config);
      if (!configResult.success) {
        ctx.addIssue({
          code: 'custom',
          path: ['config'],
          message: 'CUBEJS config must include host and tokenSecretIdentifier',
        });
      }
      if (data.secrets !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['secrets'],
          message: 'CUBEJS token must be configured via managed secret identifier in config',
        });
      }
    }
  });
export type CreateDataConnectionBody = z.infer<typeof createDataConnectionBodySchema>;

/**
 * Update Data Connection Request Body
 */
export const updateDataConnectionBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  /**
   * Secrets for the data connection (Key-Value pairs)
   * - Omitted: No change to existing secrets
   * - Empty object `{}`: Delete existing secret from AWS Secrets Manager
   * - Non-empty `{key: "value", ...}`: Create or update secret in AWS Secrets Manager
   *
   * Example: `{ password: "new-password", apiKey: "sk-987654" }`
   */
  secrets: z.record(z.string(), z.string()).optional(),
});
export type UpdateDataConnectionBody = z.infer<typeof updateDataConnectionBodySchema>;

/**
 * Data Connection Response (single)
 */
export const dataConnectionResponseSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  connectionCategory: connectionCategorySchema,
  connectionProvider: connectionProviderSchema,
  config: z.record(z.string(), z.unknown()),
  status: connectionStatusSchema,
  testResult: testResultSchema,
  secretRef: z.string().nullable(),
  lastSyncedAt: z.coerce.date().nullable().optional(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable(),
  updatedBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type DataConnectionResponse = z.infer<typeof dataConnectionResponseSchema>;

/**
 * List Data Connections Response
 */
export const listDataConnectionsResponseSchema = z.object({
  items: z.array(dataConnectionResponseSchema),
});
export type ListDataConnectionsResponse = z.infer<typeof listDataConnectionsResponseSchema>;

/**
 * Test Data Connection Request
 */
export const testDataConnectionRequestSchema = z.object({
  // No body required — test endpoint extracts identifier from URL
});

/**
 * Test Data Connection Response
 */
export const testDataConnectionResponseSchema = z.object({
  status: z.enum(['success', 'error']),
  message: z.string(),
  timestamp: z.coerce.date(),
});
export type TestDataConnectionResponse = z.infer<typeof testDataConnectionResponseSchema>;
